<script setup lang="ts">
/**
 * Закрепление чата за колонками. Закрепление исключительное: карточки из чата создают только выбранные
 * колонки — системные и личные; остальные колонки в этом чате ничего не ловят. Несколько системных —
 * вопрос раскладывается правилами между ними. Снять или изменить — здесь же или в разделе «Чаты».
 */
import { computed, ref, watch } from 'vue';
import { api } from '@/api';
import { pinPrompt } from '@/composables/usePinChat';
import { useBoard } from '@/stores/board';
import { useMeta } from '@/stores/meta';
import { useUi } from '@/stores/ui';
import { Icon, UiButton, UiCheckbox, UiModal } from '@/ui';

const meta = useMeta();
const board = useBoard();
const ui = useUi();

const selected = ref<string[]>([]);
const moveOpen = ref(true);
const saving = ref(false);

const open = computed({
  get: () => !!pinPrompt.value,
  set: (v) => {
    if (!v) pinPrompt.value = null;
  },
});

/** Закреплённые колонки, которые видит пользователь (чужие личные сервер сохранит сам). */
const current = computed(() => (pinPrompt.value?.pinnedColumns ?? []).filter((k) => meta.column(k)));

watch(pinPrompt, (p) => {
  if (!p) return;
  const cur = p.pinnedColumns.filter((k) => meta.column(k));
  selected.value = cur.length ? [...cur] : p.suggestedColumn ? [p.suggestedColumn] : [];
  moveOpen.value = true;
});

function toggle(key: string) {
  selected.value = selected.value.includes(key) ? selected.value.filter((k) => k !== key) : [...selected.value, key];
}

const same = (a: string[], b: string[]) => a.length === b.length && a.every((k) => b.includes(k));
const unchanged = computed(() => same(selected.value, current.value));
const openInChat = computed(() => board.open.filter((t) => t.chatId === pinPrompt.value?.chatId));
const willMove = computed(() => openInChat.value.filter((t) => !selected.value.includes(t.columnKey) && meta.column(t.columnKey)?.ownerUserId == null).length);
const titles = (keys: string[]) => keys.map((k) => meta.column(k)?.title ?? k).join(', ');

async function save(columnKeys: string[]) {
  const p = pinPrompt.value;
  if (!p) return;
  saving.value = true;
  try {
    const res = await api.chats.pin(p.chatId, columnKeys, moveOpen.value);
    if (columnKeys.length) {
      const parts = [];
      if (res.moved) parts.push(`перенесено карточек: ${res.moved}`);
      if (res.merged) parts.push(`объединено дублей: ${res.merged}`);
      ui.success(`Чат закреплён: ${titles(columnKeys)}`, parts.join(', ') || undefined);
    } else {
      ui.success('Закрепление снято', 'Вопросы из чата снова раскладываются по правилам');
    }
    await board.load();
    p.onDone?.(res.chat.pinnedColumnKeys);
    pinPrompt.value = null;
  } catch (e) {
    ui.error(e);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <UiModal v-model="open" title="Закрепить чат за колонками" :subtitle="pinPrompt?.chatTitle" :width="480">
    <p class="muted small lead">
      Карточки из этого чата будут создавать <b>только выбранные колонки</b> — остальные колонки, в том числе личные, в нём ничего
      не ловят. Выбрано несколько основных колонок — вопрос раскладывается правилами между ними, не подошла ни одна — в первую.
    </p>

    <div v-if="current.length" class="current">
      <Icon name="pin" :size="14" />
      Сейчас закреплён:
      <span><template v-for="(k, i) in current" :key="k"><b :style="{ color: meta.column(k)?.color }">{{ meta.column(k)?.title }}</b>{{ i < current.length - 1 ? ', ' : '' }}</template></span>
    </div>

    <div class="opts" role="group">
      <button
        v-for="c in meta.boardColumns"
        :key="c.key"
        type="button"
        role="checkbox"
        class="opt"
        :class="{ on: selected.includes(c.key) }"
        :aria-checked="selected.includes(c.key)"
        :style="{ '--c': c.color }"
        @click="toggle(c.key)"
      >
        <span class="dot" />
        <span class="grow">{{ c.title }}</span>
        <span v-if="c.ownerUserId != null" class="faint small">личная</span>
        <Icon v-if="selected.includes(c.key)" name="check" :size="15" :stroke="2.4" />
      </button>
    </div>

    <UiCheckbox v-if="willMove" v-model="moveOpen" class="move">
      Перенести открытые карточки этого чата в первую выбранную колонку
      <span class="muted">({{ willMove }})</span>
    </UiCheckbox>

    <template #footer>
      <UiButton v-if="current.length" variant="danger" :disabled="saving" @click="save([])">Снять закрепление</UiButton>
      <span class="grow" />
      <UiButton variant="ghost" @click="open = false">Отмена</UiButton>
      <UiButton variant="primary" icon="pin" :loading="saving" :disabled="!selected.length || unchanged" @click="save(selected)">
        Закрепить
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.lead {
  margin-bottom: 14px;
  line-height: 1.5;
}
.current {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  margin-bottom: 12px;
  border-radius: var(--radius-sm);
  background: var(--surface-2);
  border: 1px solid var(--border);
  font-size: 12.5px;
}
.opts {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.opt {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 10px;
  height: 42px;
  padding: 0 12px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--surface-2);
  cursor: pointer;
  font-weight: 500;
  text-align: left;
  transition: all var(--dur) var(--ease);
}
.opt:hover {
  border-color: var(--border-strong);
}
.opt.on {
  border-color: var(--c);
  background: color-mix(in srgb, var(--c) 12%, var(--surface));
  color: var(--text);
}
.dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: var(--c);
}
.move {
  margin-top: 14px;
}
</style>
